const PLACEMENT_CLASSES = {
    LOADED: 'promo-placement--loaded',
    STUB: 'promo-placement--stub',
    ERROR: 'promo-placement--error',
    VISIBLE: 'is-visible'
};

const PLACEMENT_A1_ID = 'plc.serp.a1.below_search.v1';
const PLACEMENT_TYPES = {
    BR1: 'br1',
    BR2: 'br2'
};
const ERROR_CODES_TO_HIDE = new Set([404, 402]);

class AdFoxHelper {
    constructor() {
        this.activeContainers = new Set();
    }

    extractErrorCode(error) {
        if (!error) return null;

        if (error.status !== undefined) return Number(error.status);
        if (error.code !== undefined) return Number(error.code);

        if (error.type !== undefined) {
            const parsed = Number.parseInt(error.type, 10);
            if (!Number.isNaN(parsed)) return parsed;
        }

        const message = error.message;
        if (typeof message === 'string') {
            if (message.includes('404')) return 404;
            if (message.includes('402')) return 402;
        }

        return null;
    }

    getPlacementType(placementId) {
        if (!placementId) return '';
        const parts = placementId.split('.');
        if (parts.length < 3) return '';
        return parts[2].toLowerCase();
    }

    isSidebarPlacement(placementId) {
        const type = this.getPlacementType(placementId);
        return type === PLACEMENT_TYPES.BR1 || type === PLACEMENT_TYPES.BR2;
    }

    checkAdFoxAvailability() {
        try {
            return !!(window.Ya && window.Ya.adfoxCode);
        } catch (error) {
            console.error('[AdFoxHelper] AdFox API not available:', error);
            return false;
        }
    }

    getContainer(containerId) {
        return document.querySelector(`#${containerId}`);
    }

    resetContainerStyles(container) {
        /* eslint-disable no-param-reassign */
        container.style.visibility = 'visible';
        container.style.position = '';
        container.style.left = '';
        /* eslint-enable no-param-reassign */
    }

    hideContainer(container) {
        /* eslint-disable no-param-reassign */
        container.style.visibility = 'hidden';
        container.style.display = 'block';
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        /* eslint-enable no-param-reassign */
    }

    handlePlacementState(containerId, state, isA1Placement) {
        const container = this.getContainer(containerId);
        if (!container) return;

        this.resetContainerStyles(container);

        const classMap = {
            loaded: [PLACEMENT_CLASSES.LOADED, PLACEMENT_CLASSES.VISIBLE],
            stub: [PLACEMENT_CLASSES.STUB],
            error: [PLACEMENT_CLASSES.ERROR]
        };

        const removeClasses = Object.values(PLACEMENT_CLASSES);
        container.classList.remove(...removeClasses);

        if (classMap[state]) {
            container.classList.add(...classMap[state]);
        }

        if (isA1Placement || state === 'loaded') {
            container.classList.add(PLACEMENT_CLASSES.VISIBLE);
            container.style.display = 'block';
        } else {
            container.style.display = 'none';
        }

        if (state === 'loaded') {
            this.activeContainers.add(containerId);
        }
    }

    create(containerId, customParam1, customParam2, ownerId, placementId, onSidebarError) { // eslint-disable-line max-params
        try {
            const container = this.getContainer(containerId);
            if (!container) {
                console.error(`[AdFoxHelper] Container ${containerId} not found`);
                return false;
            }

            const isA1Placement = placementId === PLACEMENT_A1_ID;
            this.hideContainer(container);

            window.yaContextCb = window.yaContextCb || [];
            window.yaContextCb.push(() => {
                if (!this.checkAdFoxAvailability()) {
                    console.warn('[AdFoxHelper] AdFox not available');
                    this.handlePlacementState(containerId, 'stub', isA1Placement);
                    return;
                }

                try {
                    const adfoxParams = {
                        ownerId,
                        containerId,
                        platform: 'desktop',
                        params: {
                            p1: customParam1,
                            p2: customParam2
                        },
                        onRender: () => {
                            this.handlePlacementState(containerId, 'loaded', isA1Placement);
                            console.log(`[AdFoxHelper] Banner rendered: ${containerId}`);
                        },
                        onStub: () => {
                            this.handlePlacementState(containerId, 'stub', isA1Placement);
                            console.warn(`[AdFoxHelper] Stub returned for ${containerId}`);
                        },
                        onError: (error) => {
                            this.handlePlacementState(containerId, 'error', isA1Placement);
                            const errStr = error == null
                                ? 'Unknown error'
                                : (typeof error === 'object' ? (error.type ? `[${error.type}] ` : '') + JSON.stringify(error.message ?? error) : String(error));
                            console.warn(`[AdFoxHelper] Error rendering ${containerId}:`, errStr);

                            const errorCode = this.extractErrorCode(error);
                            if (ERROR_CODES_TO_HIDE.has(errorCode) && this.isSidebarPlacement(placementId) && typeof onSidebarError === 'function') {
                                onSidebarError(placementId, errorCode);
                            }
                        }
                    };

                    window.Ya.adfoxCode.create(adfoxParams);
                } catch (error) {
                    console.error(`[AdFoxHelper] Error creating banner:`, error);
                    this.handlePlacementState(containerId, 'error', isA1Placement);
                }
            });

            return true;
        } catch (error) {
            console.error(`[AdFoxHelper] Error in create:`, error);
            return false;
        }
    }

    clear(containerId) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                container.innerHTML = '';
                container.style.display = 'none';
                container.style.visibility = '';
                container.style.position = '';
                container.style.left = '';
                container.classList.remove(...Object.values(PLACEMENT_CLASSES));
                this.activeContainers.delete(containerId);
                console.log(`[AdFoxHelper] Container ${containerId} cleared`);
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error clearing:`, error);
        }
    }

    show(containerId) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                container.style.display = 'block';
                container.classList.add(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error showing:`, error);
        }
    }

    hide(containerId) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                container.style.display = 'none';
                container.classList.remove(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error hiding:`, error);
        }
    }
}

let adFoxHelperInstance = null;

export default {
    getInstance() {
        if (!adFoxHelperInstance) {
            adFoxHelperInstance = new AdFoxHelper();
        }
        return adFoxHelperInstance;
    }
};
